import Select from 'react-select';
import Switch from "react-switch";
import constants from './constants.json';
import dropdownOptions from './dropdownOptions.json';

// When some toggles are enabled, then associated dropdowns must get a
// default value.
let defaultOptions = {
    'menuEnabled': {
        'menu': constants.DEFAULT_INTERACTIVITY_CONFIGS.menu
    },
    'navBarEnabled': {
        'barPosition': constants.DEFAULT_INTERACTIVITY_CONFIGS.barPosition,
        'openTo': constants.DEFAULT_INTERACTIVITY_CONFIGS.openTo,
    }
};

const InteractivityConfiguration = ({ state, setState, setStateChanged, permissions, user}) => {
    const configs = state?.playlist?.interactivityConfigs || {};
    const defaultConfigs = constants.DEFAULT_INTERACTIVITY_CONFIGS;

    const isFeatureEnabled = (feature) => {
        return feature in permissions && state?.playlist?.location?.id in permissions[feature];
    }

    const isInputEnabled = (feature) => {
        return isFeatureEnabled(feature) && permissions[feature][state.playlist?.location?.id].includes('editor');
    }

    const handleConfigChanges = (keys, values) => {
        const tempConfigs = {...state.playlist.interactivityConfigs};
        // Just one item to change
        if (keys.length <= 1) {
            keys.forEach((key, index) => {
                tempConfigs[key] = values[index];
            });
        }
        //If multiple, we need to check if the toggle was turned on or off. If off, we need to remove default params
        else {
            // Toggle was turned on
            if (values[0]) {
                keys.forEach((key, index) => {
                    tempConfigs[key] = values[index];
                });
            }
            else {
                keys.forEach((key, index) => {
                    // Only apply the first value, as it is the main toggle
                    if (index === 0) {
                        tempConfigs[key] = values[index];
                    }
                    else {
                        delete tempConfigs[key];
                    }
                });
            }
        }

        setState({
            ...state,
            playlist: {
                ...state.playlist,
                interactivityConfigs: tempConfigs
            },
        });
        setStateChanged(true);
    }

    const getLabelAndSwitch = (label, key, featureKey) => {
        let checkValue = configs && key in configs ? configs[key] : defaultConfigs[key];

        return <div className="input-container">
            <div className="label">{label}:</div>
            <Switch
                className="switch"
                checked={ checkValue }
                onChange={ (checked) => handleConfigChanges([key], [checked]) }
                onColor="#2386EE"
                uncheckedIcon
                checkedIcon={false}
                disabled={!isInputEnabled(featureKey)}
            />
        </div>
    }

    /*
     * These switches enable/disable a dropdown
     */
    const getLabelAndSwitchToggle = (label, key, featureKey) => {
        let onChange = (checked) => handleConfigChanges([key], [checked]);

        // If we are enabling some toggles for the first time, set any
        // relevant default values.
        if (Object.keys(defaultOptions).includes(key)) {
            let moreKeys = [];
            let moreValues = [];
            for (let [nestedKey, value] of Object.entries(defaultOptions[key])) {
                moreKeys.push(nestedKey);
                moreValues.push(value);
            }
            onChange = (checked) => handleConfigChanges([key].concat(moreKeys), [checked].concat(moreValues));
        }

        return <div className="input-container">
            <div className="label">{label}:</div>
            <Switch
                className="switch"
                checked={configs && key in configs ? configs[key] : false}
                onChange={onChange}
                onColor="#2386EE"
                uncheckedIcon
                checkedIcon={false}
                disabled={!isInputEnabled(featureKey)}
            />
        </div>
    }

    /**
     * dropdownKey - This key in dropdownOptions.json file contains the dropdown options
     *     that appear in the <Select> component
     * configKey - The key in the `interactivityConfigs` json
     * dependencyKey - What other key needs to be enabled before this dropdown is enabled
     */
    const getLabelAndSelect = (label, dropdownKey, configKey, dependencyKey) => {
        let classNames = configs && dependencyKey in configs && configs[dependencyKey] ? '' : 'disabled-section';

        return <div className="input-container">
            <div className="label">{label}:</div>
            <Select
                onChange={ (e) => handleConfigChanges([configKey], [e.value]) }
                defaultValue={dropdownOptions[dropdownKey][0]}
                options={dropdownOptions[dropdownKey]}
                value={dropdownOptions[dropdownKey].find(option => option.value === (configs ? configs[configKey] : defaultConfigs[configKey]))}
                className={`dropdown-list ${classNames}`}
                styles={basicConfigDropdownStyling}
                isDisabled={!isInputEnabled('transportationConfig')}
            />
        </div>
    }

    /* React-Select custom styling */
    const basicConfigDropdownStyling = {
        container: (baseStyles, state) => ({
          ...baseStyles,
          // Too much gray if the dropdown is disabled.
          boxShadow: state.isDisabled ? "unset" : "0px 2px 8px 4px rgba(0, 0, 0, 0.07)",
          borderRadius: "8px"
        }),
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: "none",
            cursor: "pointer"
        }),
        indicatorSeparator: (baseStyles, state) => ({
            ...baseStyles,
            display: "none"
        }),
        option: (baseStyles, state) => ({
            ...baseStyles,
            color: state.isDisabled ? "#919191" : "inherit",
            cursor: "pointer"
        })
    };

    return (
        <div className="InteractivityConfiguration">
            <div className="photo">
            </div>
            <div className="configuration">
                <h2>Interactivity</h2>

                <div className={`section ${!isFeatureEnabled('qrCodes') || !['portrait', 'landscape'].includes(state?.playlist?.deviceType) ? 'disabled-section' : '' }`}>
                    <h3>Content on the go QR codes</h3>
                    <div className="description">
                        Enable people to interact with your content like never before. For each view you display (TransitScreen, Nearby, etc), a QR code will appear that allows the passerby to take that content with them in seconds on their mobile phone.
                    </div>
                    <div>
                        { getLabelAndSwitch('Enable', 'qrCode', 'qrCodes') }
                    </div>
                </div>

                <div className={`section ${!isFeatureEnabled('transportationConfig') || state?.playlist?.deviceType !== 'mobile' ? 'disabled-section' : ''}`}>
                    <h3>TransitScreen&reg; Search and weather navigation bar</h3>
                    <div>
                        { getLabelAndSwitchToggle('Show', 'menuEnabled', 'transportationConfig') }
                        { getLabelAndSelect('Type', 'TYPE', 'menu', 'menuEnabled') }
                    </div>
                </div>

                <div className={`section ${!isFeatureEnabled('transportationConfig') || state?.playlist?.deviceType !== 'mobile' ? 'disabled-section' : ''}`}>
                    <h3>TransitScreen&reg; mode and trip planner navigation bar</h3>
                    { getLabelAndSwitchToggle('Show', 'navBarEnabled', 'transportationConfig') }
                    { getLabelAndSelect('Location', 'BAR_POSITION', 'barPosition', 'navBarEnabled')}
                    { getLabelAndSelect('Opening Mode', 'OPEN_TO', 'openTo', 'navBarEnabled') }
                </div>

                <div className={`section ${!isFeatureEnabled('tripPlanner') || state?.playlist?.deviceType !== 'mobile' ? 'disabled-section' : ''}`}>
                    <h3>Trip planner</h3>
                    <div className="description">
                        Help users get from A to B using our multi-modal trip planner feature. When enabled, a Trip Planner button will appear in the mode and trip planner navigation bar. When tapped, the user will be able to enter their destination and will be provided with a number of options to get them there.
                    </div>
                    <div>
                        { getLabelAndSwitch('Show', 'tripPlan', 'tripPlanner') }
                    </div>
                </div>

                <div className={`section ${state?.playlist?.deviceType !== 'mobile' && state?.playlist?.interactivityConfigs?.qrCode === false ? 'disabled-section' : ''}`}>
                    <h3>Advanced mobile interactivity</h3>
                    <div className="description">
                        These features allow users to indicate their favorite stops/stations, tap into a "card" to see a map with enhanced arrivals information, and even turn on/off the ability to open other transportation apps (Uber, Lyft, etc) at the press of a button.
                    </div>
                    <div>
                        { getLabelAndSwitch('Enable', 'interactive', 'interactivityConfiguration') }
                        { getLabelAndSwitch('Favorites', 'favorites', 'interactivityConfiguration') }
                        { getLabelAndSwitch('Maps', 'maps', 'interactivityConfiguration') }
                        { getLabelAndSwitch('Other apps', 'externalLinks', 'interactivityConfiguration') }
                    </div>
                </div>
            </div>
        </div>
    )
}


export default InteractivityConfiguration;
